import emit from '../../../../common/events';
import store from '../../../../store/store';
import { FilterAction } from '../interfaces/create-search-filter.interface';
import { SearchFacet, SearchFacetValue } from '../../../search/interfaces/search.interface';
import { Select } from '../../../selector/selector';
import { connect } from 'pwa-helpers';
import { createFacet, getFacetValuesAsObject } from '../utils/create-search-filters.util';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('mhecomm-create-search-dropdown-filter')
export class CreateSearchDropdownFilter extends connect(store)(Select) {
  /**
   * The action to take when the filter is changed.
   * Defaults to changing the filter in the search, but not clearing other filters.
   */
  @property()
  actionChange = FilterAction.CHANGE;

  /**
   * The action to take when the filter is reset.
   * Defaults to removing the filter from the search.
   */
  @property()
  actionReset = FilterAction.REMOVE;

  /**
   * The SearchFacet object that contains the facet values to display in the dropdown.
   */
  @property({ type: Object })
  facet?: SearchFacet;

  @state()
  previousOption = '';

  connectedCallback(): void {
    super.connectedCallback();
    this.options = getFacetValuesAsObject(this.facet?.facetValues) ?? {};
  }

  /**
   * If the facet is updated, changes the dropdown selection and preserves the previous option.
   *
   * @param _changedProperties
   */
  protected async updated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
    super.updated(_changedProperties);
    if (_changedProperties.has('facet')) {
      this.options = getFacetValuesAsObject(this.facet?.facetValues) ?? {};
      const selectedValue = this.facet?.facetValues.find((facetValue: SearchFacetValue) => facetValue.selected);
      this.previousOption = this.selectedOption;
      this.selectedOption = selectedValue?.name ?? this.defaultOptionFormValue;
    }
  }

  /**
   * If the default option is selected, the filter will be reset.
   * Otherwise, the filter will be changed to the selected option.
   */
  protected onSelectOption(): void {
    if (this.selectedOption === this.defaultOptionFormValue) {
      this.triggerFilterAction(this.actionReset, this.previousOption);
    } else {
      this.triggerFilterAction(this.actionChange, this.selectedOption);
    }
  }

  /**
   * Triggers a custom event to update the search filters based on the selected option and action.
   * The changes to the search request are made from the parent component.
   *
   * @param action
   * @param option
   */
  protected triggerFilterAction(action: FilterAction, option: string): void {
    const facetValue =
      this.facet?.facetValues.find((value) => value.name === option) ??
      createFacet(this.defaultOptionFormValue, this.defaultOptionFormValue, []);

    emit({
      element: this,
      name: 'mhecomm-create-search-filter-selection',
      detail: {
        action: action,
        facet: this.facet,
        value: facetValue,
      },
      dataLayer: false,
      bubbles: true,
      composed: true,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-search-dropdown-filter': CreateSearchDropdownFilter;
  }
}
